/**
 * © 2026 Little Shilling, Inc.
 * Shon Little
 * Created: 2026-05-04
 */

// Add third-party dependencies.
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Unstable_Grid2 as Grid,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridRowModes, GridRowEditStopReasons } from '@mui/x-data-grid';
import { Save as SaveIcon, Delete as DeleteIcon, Cancel as CancelIcon, Edit as EditIcon } from '@mui/icons-material';

// Add local dependencies.
import AuthMenuGroup from '../AuthMenuGroup';
import {
  useGetAllPayRatesQuery,
  useAddPayRateMutation,
  useUpdatePayRateMutation,
  useDeletePayRateMutation,
} from '../../../api/payrollSlice';
import { useGetUsersQuery } from '../../../api/authSlice';
import PageSpinner from '../../Common/PageSpinner';
import ErrorAlert from '../../Common/ErrorAlert';
import Question from '../../Common/Question';

/**
 * PayRates admin page. CRUD for staff pay rates.
 * @returns {React.ReactElement} component.
 */
const PayRates = () => {
  // Set form hooks for the add row.
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { employees: [], rate_name: '', rate: '', flat_rate: false, minimum: '' },
  });

  // Set query/mutation hooks.
  const {
    isLoading: isLoadingRates,
    isError: isErrorRates,
    error: errorRates,
    data: payRates = [],
  } = useGetAllPayRatesQuery();
  const { isLoading: isLoadingUsers, isError: isErrorUsers, error: errorUsers, data: users = [] } = useGetUsersQuery();
  const [addPayRate, { isLoading: isAdding }] = useAddPayRateMutation();
  const [updatePayRate, { isLoading: isUpdating }] = useUpdatePayRateMutation();
  const [deletePayRate, { isLoading: isDeleting }] = useDeletePayRateMutation();

  // Set state hooks.
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowToDelete, setRowToDelete] = useState(null);
  const [errorAlert, setErrorAlert] = useState(null);

  // Handle row mode transitions.
  const handleRowModesModelChange = (next) => setRowModesModel(next);
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true; // eslint-disable-line no-param-reassign
    }
  };
  const handleEditClick = (id) => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  const handleSaveClick = (id) => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  const handleCancelClick = (id) => () =>
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

  // Process inline row update.
  const processRowUpdate = async (newRow, oldRow) => {
    try {
      if (!newRow.rate_name?.trim()) throw new Error('Rate name is required.');
      const rate = Number(newRow.rate);
      if (Number.isNaN(rate) || rate <= 0) throw new Error('Rate must be greater than 0.');
      const minimum = newRow.minimum === '' || newRow.minimum === null ? null : Number(newRow.minimum);
      if (minimum !== null && Number.isNaN(minimum)) throw new Error('Minimum must be a number.');
      return await updatePayRate({
        id: newRow.id,
        user: newRow.user, // locked column; carried through unchanged
        rate_name: newRow.rate_name.trim(),
        rate,
        flat_rate: !!newRow.flat_rate,
        minimum,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update pay rate:', error);
      setErrorAlert(error);
      return oldRow;
    }
  };

  // Handle delete flow.
  const handleDeleteClick = (id) => () => {
    const row = payRates.find((r) => r.id === id);
    if (row) setRowToDelete(row);
  };
  const handleConfirmDelete = async () => {
    const target = rowToDelete;
    setRowToDelete(null);
    if (!target) return;
    try {
      await deletePayRate(target.id).unwrap();
    } catch (error) {
      console.error('Failed to delete pay rate:', error);
      setErrorAlert(error);
    }
  };
  const handleCancelDelete = () => setRowToDelete(null);

  // Handle add form submission.
  const onSubmit = async ({ employees, rate_name: rateName, rate, flat_rate: flatRate, minimum }) => {
    const trimmedName = rateName.trim();
    const rateValue = Number(rate);
    const minimumValue = minimum === '' || minimum === null || minimum === undefined ? null : Number(minimum);
    try {
      await Promise.all(
        employees.map((employee) =>
          addPayRate({
            user: employee.id,
            rate_name: trimmedName,
            rate: rateValue,
            flat_rate: !!flatRate,
            minimum: minimumValue,
          }).unwrap()
        )
      );
      reset({ employees: [], rate_name: '', rate: '', flat_rate: false, minimum: '' });
    } catch (error) {
      console.error('Failed to add pay rate(s):', error);
      setErrorAlert(error);
    }
  };

  // Loading / error gates.
  if (isLoadingRates || isLoadingUsers) return <PageSpinner />;
  if (isErrorUsers) return <ErrorAlert error={errorUsers} fallback="Error loading employees." />;
  if (isErrorRates) return <ErrorAlert error={errorRates} fallback="Error loading pay rates." />;

  // Sort employees for the autocomplete.
  const employeeOptions = [...users].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  );

  // Columns for the rates grid.
  const columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 110,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem key="save" icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} />,
            <GridActionsCellItem key="cancel" icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} />,
          ];
        }
        return [
          <GridActionsCellItem key="edit" icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} />,
          <GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} />,
        ];
      },
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'user_name', headerName: 'Employee', width: 180 },
    { field: 'rate_name', headerName: 'Rate Name', width: 220, editable: true },
    {
      field: 'rate',
      headerName: 'Rate',
      type: 'number',
      width: 110,
      editable: true,
      valueFormatter: (value) =>
        value === null || value === undefined || value === '' ? '' : `$${Number(value).toFixed(2)}`,
    },
    { field: 'flat_rate', headerName: 'Per hour?', type: 'boolean', width: 110, editable: true },
    {
      field: 'minimum',
      headerName: 'Minimum',
      type: 'number',
      width: 110,
      editable: true,
      valueFormatter: (value) =>
        value === null || value === undefined || value === '' ? '' : `$${Number(value).toFixed(2)}`,
    },
    { field: 'activities_count', headerName: 'Activities', type: 'number', width: 100 },
  ];

  // Render component.
  return (
    <AuthMenuGroup>
      {(isAdding || isUpdating || isDeleting) && <PageSpinner />}
      <Box>
        <Typography variant="h1">Pay Rates</Typography>
      </Box>
      {errorAlert && (
        <ErrorAlert error={errorAlert} fallback="An unexpected error occurred. Please try again later." />
      )}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ width: '100%' }}>
        <Grid container spacing={1}>
          <Grid xs={12} md={4}>
            <Controller
              name="employees"
              control={control}
              rules={{ validate: (value) => (value && value.length > 0) || 'At least one employee is required.' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  disablePortal
                  options={employeeOptions}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={field.value}
                  onChange={(event, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      label="Employees"
                      margin="dense"
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid xs={12} md={3}>
            <Controller
              name="rate_name"
              control={control}
              rules={{ required: 'Rate name is required.', maxLength: { value: 255, message: '255 chars max.' } }}
              render={({ field, fieldState }) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  label="Rate Name"
                  fullWidth
                  margin="dense"
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                />
              )}
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <Controller
              name="rate"
              control={control}
              rules={{
                required: 'Rate is required.',
                validate: (value) => Number(value) > 0 || 'Rate must be greater than 0.',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  label="Rate ($)"
                  type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                  fullWidth
                  margin="dense"
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                />
              )}
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <Controller
              name="flat_rate"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />
                  }
                  label="Per hour"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Grid>
          <Grid xs={6} md={1.5}>
            <Controller
              name="minimum"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  label="Minimum ($)"
                  type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                  fullWidth
                  margin="dense"
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                />
              )}
            />
          </Grid>
          <Grid xs={6} md={0.5}>
            <Button type="submit" fullWidth variant="contained" sx={{ marginTop: 2 }} disabled={isAdding}>
              Add
            </Button>
          </Grid>
        </Grid>
        {errors.employees && (
          <Typography color="error" variant="caption">
            {errors.employees.message}
          </Typography>
        )}
      </Box>
      <Box mt={2}>
        <DataGrid
          rows={payRates}
          columns={columns}
          autoHeight
          density="compact"
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          pageSizeOptions={[25, 50, 100, 200, 500]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'user_name', sort: 'asc' }] },
          }}
        />
      </Box>
      <Question
        title="Delete pay rate?"
        question={
          rowToDelete
            ? `"${rowToDelete.rate_name}" (${rowToDelete.user_name}) is used by ${rowToDelete.activities_count} activit${rowToDelete.activities_count === 1 ? 'y' : 'ies'}. Deleting it hides it from new entries; existing pay records stay intact. Delete it?`
            : ''
        }
        option1="Delete"
        option2="Cancel"
        option1Handler={handleConfirmDelete}
        option2Handler={handleCancelDelete}
        open={!!rowToDelete}
        onClose={handleCancelDelete}
      />
    </AuthMenuGroup>
  );
};

// Export component.
export default PayRates;
